import type { PmProgram, PmProgramAsset } from "@/hooks/usePmPrograms";

interface OrgInfo {
  name: string | null;
  logo_url: string | null;
}

type SortMode = "location" | "workflow";

function buildAssetRow(a: PmProgramAsset, secondCol: string) {
  const makeModel = [a.make, a.model].filter(Boolean).join(" ") || "—";
  return `
    <tr>
      <td style="padding:9px 10px;border-bottom:1px solid #f0f0f0;">${a.name}</td>
      <td style="padding:9px 10px;border-bottom:1px solid #f0f0f0;text-transform:capitalize;">${secondCol}</td>
      <td style="padding:9px 10px;border-bottom:1px solid #f0f0f0;font-family:monospace;font-size:11px;">${a.serial_number ?? "—"}</td>
      <td style="padding:9px 10px;border-bottom:1px solid #f0f0f0;">${makeModel}</td>
      <td style="padding:9px 10px;border-bottom:1px solid #f0f0f0;text-align:center;">
        <div style="width:18px;height:18px;border:1.5px solid #999;border-radius:3px;display:inline-block;"></div>
      </td>
      <td style="padding:9px 10px;border-bottom:1px solid #f0f0f0;width:120px;">
        <div style="border-bottom:1px solid #ddd;width:100%;height:18px;"></div>
      </td>
      <td style="padding:9px 10px;border-bottom:1px solid #f0f0f0;width:160px;">
        <div style="border-bottom:1px solid #ddd;width:100%;height:18px;"></div>
      </td>
    </tr>`;
}

function buildGroupTable(sectionLabel: string, rows: string, secondColHeader: string) {
  return `
    <div style="margin-bottom:6px;">
      <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.6px;color:#777;padding:6px 0 4px 2px;">
        ${sectionLabel}
      </div>
      <div style="border:1px solid #e8e8e8;border-radius:8px;overflow:hidden;margin-bottom:10px;">
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f8f8f8;">
              <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#888;">Asset</th>
              <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#888;">${secondColHeader}</th>
              <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#888;">Serial #</th>
              <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#888;">Make / Model</th>
              <th style="padding:8px 10px;text-align:center;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#888;">Done</th>
              <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#888;">Date Completed</th>
              <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#888;">Notes / Initials</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>`;
}

export function printPmWorksheet(
  program: PmProgram,
  assets: PmProgramAsset[],
  org: OrgInfo | null,
  sortMode: SortMode = "location",
) {
  const generatedDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const logoHtml = org?.logo_url
    ? `<img src="${org.logo_url}" alt="Logo" style="max-height:56px;max-width:180px;object-fit:contain;" />`
    : `<span style="font-size:20px;font-weight:700;color:#1a1a1a;">${org?.name ?? "Gravity Wine House"}</span>`;

  let facilityBlocks = "";

  if (sortMode === "location") {
    // Group by facility → zone
    const byFacility = new Map<string, { facilityName: string; zones: Map<string, { zoneName: string; assets: PmProgramAsset[] }> }>();
    for (const a of assets) {
      const fKey = a.facility_id ?? "__none__";
      const fName = a.facility_name ?? "No Facility";
      const zKey = a.zone_id ?? "__none__";
      const zName = a.zone_name ?? "No Zone";
      if (!byFacility.has(fKey)) byFacility.set(fKey, { facilityName: fName, zones: new Map() });
      const fGroup = byFacility.get(fKey)!;
      if (!fGroup.zones.has(zKey)) fGroup.zones.set(zKey, { zoneName: zName, assets: [] });
      fGroup.zones.get(zKey)!.assets.push(a);
    }
    for (const fGroup of byFacility.values())
      for (const zGroup of fGroup.zones.values())
        zGroup.assets.sort((a, b) => (a.asset_type ?? "").localeCompare(b.asset_type ?? "") || a.name.localeCompare(b.name));

    facilityBlocks = Array.from(byFacility.values()).map(({ facilityName, zones }) => {
      const sectionBlocks = Array.from(zones.values()).map(({ zoneName, assets: zAssets }) => {
        const rows = zAssets.map((a) => buildAssetRow(a, a.asset_type?.replace(/_/g, " ") ?? "—")).join("");
        return buildGroupTable(zoneName, rows, "Type");
      }).join("");
      return `
        <div style="margin-bottom:28px;page-break-inside:avoid;">
          <div style="font-size:15px;font-weight:700;color:#1a1a1a;padding:8px 0 6px 0;border-bottom:2px solid #1a1a1a;margin-bottom:10px;">${facilityName}</div>
          ${sectionBlocks}
        </div>`;
    }).join("");

  } else {
    // Group by facility → asset type
    const byFacility = new Map<string, { facilityName: string; types: Map<string, { typeName: string; assets: PmProgramAsset[] }> }>();
    for (const a of assets) {
      const fKey = a.facility_id ?? "__none__";
      const fName = a.facility_name ?? "No Facility";
      const tKey = a.asset_type ?? "__none__";
      const tName = a.asset_type ? a.asset_type.replace(/_/g, " ") : "Unclassified";
      if (!byFacility.has(fKey)) byFacility.set(fKey, { facilityName: fName, types: new Map() });
      const fGroup = byFacility.get(fKey)!;
      if (!fGroup.types.has(tKey)) fGroup.types.set(tKey, { typeName: tName, assets: [] });
      fGroup.types.get(tKey)!.assets.push(a);
    }
    for (const fGroup of byFacility.values())
      for (const tGroup of fGroup.types.values())
        tGroup.assets.sort((a, b) => a.name.localeCompare(b.name));

    facilityBlocks = Array.from(byFacility.values()).map(({ facilityName, types }) => {
      const sectionBlocks = Array.from(types.values()).map(({ typeName, assets: tAssets }) => {
        const rows = tAssets.map((a) => buildAssetRow(a, a.zone_name ?? "—")).join("");
        return buildGroupTable(typeName, rows, "Zone");
      }).join("");
      return `
        <div style="margin-bottom:28px;page-break-inside:avoid;">
          <div style="font-size:15px;font-weight:700;color:#1a1a1a;padding:8px 0 6px 0;border-bottom:2px solid #1a1a1a;margin-bottom:10px;">${facilityName}</div>
          ${sectionBlocks}
        </div>`;
    }).join("");
  }

  const noAssets = assets.length === 0
    ? `<div style="padding:32px;text-align:center;color:#aaa;font-size:14px;">No assets assigned to this program.</div>`
    : "";

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${program.name} — PM Worksheet</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 13px; color: #1a1a1a; background: #fff; }
    .page { max-width: 1100px; margin: 0 auto; padding: 36px 48px 56px; }
    @media print {
      @page { size: landscape; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { padding: 20px 32px 40px; }
      .page-break { page-break-before: always; }
    }
  </style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:24px;border-bottom:2px solid #e8e8e8;">
    <div>${logoHtml}</div>
    <div style="text-align:right;">
      <div style="font-size:22px;font-weight:700;letter-spacing:-0.5px;">${program.name}</div>
      <div style="font-size:12px;color:#888;margin-top:3px;">Preventive Maintenance Worksheet</div>
      <div style="font-size:11px;color:#aaa;margin-top:2px;">Generated ${generatedDate}</div>
    </div>
  </div>

  <!-- Sign-off row -->
  <div style="display:flex;gap:48px;margin-bottom:32px;">
    <div>
      <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.6px;color:#999;margin-bottom:4px;">Completed By</div>
      <div style="border-bottom:1px solid #ccc;width:200px;height:22px;"></div>
    </div>
    <div>
      <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.6px;color:#999;margin-bottom:4px;">Date</div>
      <div style="border-bottom:1px solid #ccc;width:140px;height:22px;"></div>
    </div>
    <div>
      <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.6px;color:#999;margin-bottom:4px;">Signature</div>
      <div style="border-bottom:1px solid #ccc;width:200px;height:22px;"></div>
    </div>
  </div>

  <!-- Asset checklist grouped by facility/zone -->
  ${noAssets}
  ${facilityBlocks}

  <!-- Footer -->
  <div style="border-top:1px solid #e8e8e8;padding-top:14px;margin-top:16px;display:flex;justify-content:space-between;font-size:10px;color:#bbb;">
    <span>${org?.name ?? "Gravity Wine House"} — ${program.name}</span>
    <span>${assets.length} asset${assets.length !== 1 ? "s" : ""}</span>
  </div>

</div>
<script>window.onload = () => window.print();</script>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
